/**
 * @file ImageComponent.tsx
 * @description
 * -----------------------------------------------------
 * Componente de Image
 * -----------------------------------------------------
 * @version 1.0.0a
 * @created 2025-02-25
 * @modified 2026-03-02
 * @author Correcta
 * @copyright © Correcta
 * @license Private
 * */

"use client";
import { ImageProps } from "@/types/ui";
import { CircleX } from "lucide-react";
import Image from "next/image";
import React, { useState } from "react";
import ButtonComponent from "@/components/ui/ButtonComponent";
import { useTheme } from "@/context/ThemeProvider";
import { useTranslations } from "next-intl";
import { TYPE_BUTTON, BUTTON_STATUS } from "@/constants/button";
import { BUTTON_LABELS } from "@/constants/buttonLabels";
import { ENV } from "@/constants/env";

const ImageComponent: React.FC<ImageProps> = ({
  src,
  alt,
  width = 40,
  height = 40,
  className = "",
  zoomable = true,
  onError,
  onClick,
  srcDark,
  noTranslation = false,
  priority = false,
  imageTextNotLoad = false,
  decorative = false,
}) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [hasError, setHasError] = useState(false);
  const { isDark } = useTheme();
  const altT = useTranslations("Alt");
  const ariaLabelT = useTranslations("AriaLabel");

  // Detectar si la imagen es base64
  const isBase64 = typeof src === "string" && src.startsWith("data:");

  const handleOpenPreview = () => {
    if (zoomable) setIsPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setIsPreviewOpen(false);
  };
  const handleImageError = () => {
    setHasError(true);
    if (onError) onError();
  };

  const getProxiedSrc = (originalSrc: string | undefined) => {
    if (!originalSrc) return "";
    const minioHost = ENV.NEXT_PUBLIC_MINIO_HOST;
    if (minioHost && originalSrc.includes(minioHost)) {
      return `/api/proxy-image?url=${encodeURIComponent(originalSrc)}`;
    }
    return originalSrc;
  };

  const finalSrc = getProxiedSrc(isDark && srcDark ? srcDark : src);
  const isProxied = finalSrc.startsWith("/api/proxy-image?");
  const resolvedAlt = decorative ? "" : noTranslation ? alt : altT(alt);
  const role = decorative ? undefined : "img";
  const ariaHidden = decorative || undefined;
  // const resolvedTabIndex = tabIndex ?? (zoomable || onClick ? 0 : undefined);
  const handleActivate = () => {
    if (onClick) {
      onClick();
      return;
    }
    handleOpenPreview();
  };

  return (
    <>
      {!hasError ? (
        isBase64 ? (
          // Usar <img> nativo para imágenes base64
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={finalSrc}
            alt={resolvedAlt}
            width={width}
            height={height}
            role={role}
            aria-hidden={ariaHidden}
            className={
              className === "sidebar__img"
                ? className
                : `image ${zoomable ? "image--zoomable" : ""} ${className}`
            }
            onClick={decorative ? undefined : handleActivate}
            onError={handleImageError}
            style={{ objectFit: "cover" }}
          />
        ) : (
          // Usar Image de Next.js para URLs normales
          <Image
            src={finalSrc}
            alt={resolvedAlt}
            width={width}
            height={height}
            role={role}
            aria-hidden={ariaHidden}
            className={
              className === "sidebar__img"
                ? className
                : `image ${zoomable ? "image--zoomable" : ""} ${className}`
            }
            onClick={decorative ? undefined : handleActivate}
            onError={handleImageError}
            unoptimized={isProxied}
            priority={priority}
          />
        )
      ) : (
        <figure className="image-fallback">
          <Image
            src="/assets/svg/empty.svg"
            alt={resolvedAlt}
            width={width}
            height={height}
            role={role}
            aria-hidden={ariaHidden}
            className={
              className === "sidebar__img"
                ? className
                : `image ${zoomable ? "image--zoomable" : ""} ${className}`
            }
            onError={handleImageError}
            unoptimized={isProxied}
            priority={priority}
          />
          {imageTextNotLoad && (
            <figcaption className="image-fallback__text">
              {altT("imageNotLoad")}
            </figcaption>
          )}
        </figure>
      )}

      {isPreviewOpen && (
        <dialog className="image__preview">
          <ButtonComponent
            label={BUTTON_LABELS.CLOSE}
            type={TYPE_BUTTON}
            onClick={handleClosePreview}
            status={BUTTON_STATUS.PRIMARY}
            className="image__preview__close"
            ariaLabel={ariaLabelT("closeModal")}
          >
            <CircleX size={50} />
          </ButtonComponent>
          {isBase64 ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={finalSrc}
              alt={resolvedAlt}
              aria-hidden={ariaHidden}
              className="image__preview__content"
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
          ) : (
            <Image
              src={finalSrc}
              alt={resolvedAlt}
              aria-hidden={ariaHidden}
              width={1200}
              height={800}
              className="image__preview__content"
              unoptimized={isProxied}
            />
          )}
        </dialog>
      )}
    </>
  );
};

export default ImageComponent;
